/**
 * State-machine CSV parser.
 *
 * Handles: quoted fields (commas inside quotes), escaped "" in quotes,
 * BOM stripping, CRLF/LF line endings, and delimiter auto-detection
 * (comma, semicolon, tab).
 */

export interface ParsedCSV {
  headers: string[];
  rows: string[][];
  rowCount: number;
}

/** Auto-detect delimiter by counting occurrences in the first line. */
function detectDelimiter(firstLine: string): string {
  const counts: Record<string, number> = { ',': 0, ';': 0, '\t': 0 };
  let inQuotes = false;
  for (const ch of firstLine) {
    if (ch === '"') { inQuotes = !inQuotes; continue; }
    if (!inQuotes && ch in counts) counts[ch]++;
  }
  if (counts['\t'] >= counts[','] && counts['\t'] >= counts[';']) return '\t';
  if (counts[';'] > counts[',']) return ';';
  return ',';
}

export function parseCSV(text: string): ParsedCSV {
  // Strip BOM
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  if (!src.trim()) return { headers: [], rows: [], rowCount: 0 };

  const firstLine = src.slice(0, src.indexOf('\n') === -1 ? src.length : src.indexOf('\n'));
  const delim = detectDelimiter(firstLine);

  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let i = 0;

  while (i < src.length) {
    const ch = src[i];

    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < src.length && src[i + 1] === '"') {
          field += '"';
          i += 2;
        } else {
          inQuotes = false;
          i++;
        }
      } else {
        field += ch;
        i++;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
        i++;
      } else if (ch === delim) {
        row.push(field.trim());
        field = '';
        i++;
      } else if (ch === '\r') {
        // Skip \r, handled with \n
        i++;
      } else if (ch === '\n') {
        row.push(field.trim());
        field = '';
        if (row.some((c) => c !== '')) rows.push(row);
        row = [];
        i++;
      } else {
        field += ch;
        i++;
      }
    }
  }

  // Last field / last row
  row.push(field.trim());
  if (row.some((c) => c !== '')) rows.push(row);

  if (rows.length === 0) return { headers: [], rows: [], rowCount: 0 };

  const headers = rows[0];
  const dataRows = rows.slice(1);
  return { headers, rows: dataRows, rowCount: dataRows.length };
}
